import { useState, useEffect, useRef, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LangContext,
  detectLang,
  persistLang,
  type Lang,
  isPublicEnMirror,
  isPublicZhMirror,
  toEnMirrorPath,
  toZhMirrorPath,
} from './i18n-core';

interface LangProviderProps {
  children: ReactNode;
  initialLang?: Lang;
}

export function LangProvider({ children, initialLang }: LangProviderProps) {
  const [lang, setLangState] = useState<Lang>(initialLang ?? 'en');
  const navigate = useNavigate();
  const location = useLocation();

  // Autodetect only when caller hasn't provided an initialLang. With
  // explicit initial value (SSR pass + main.tsx pass), we trust the caller
  // and skip the post-mount detection race.
  useEffect(() => {
    if (initialLang !== undefined) return;
    const id = window.setTimeout(() => {
      setLangState(detectLang());
    }, 0);
    return () => window.clearTimeout(id);
  }, [initialLang]);

  useEffect(() => {
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en';
    if (lang === 'zh') {
      import('@fontsource/noto-sans-sc/400.css');
      import('@fontsource/noto-sans-sc/700.css');
    }
  }, [lang]);

  // M2.4 reactivity: when the URL path actually changes (browser back/forward,
  // a <Link> click that bypasses setLang, an OAuth redirect, etc.), the lang
  // state must follow — BUT ONLY when the path is in the public mirror set.
  // A naive "any non-zh path → setLang('en')" would flip lang to en every
  // time a zh-preference user navigated into a private route, ruining
  // language stickiness.
  //
  // Use a previous-path ref (NOT an isFirstRun boolean) so React Strict Mode's
  // dev-only double-invoke of effects doesn't accidentally re-trigger the
  // sync logic with stale state. With a boolean, run #1 sets flag=false then
  // returns; the immediate Strict-Mode re-run sees flag=false and runs the
  // sync — flipping initialLang='zh' to 'en' on en-mirror paths and breaking
  // smoke.spec.ts. With path comparison, the re-run sees prevPath === path
  // and no-ops. detectLang already considered path/navigator/localStorage/URL
  // signals at initial mount; this effect only needs to react to subsequent
  // path *changes*.
  // Strip trailing slashes (but keep root '/') so `/app/privacy/` is
  // treated identically to `/app/privacy` for mirror lookup. Without
  // this, the path-sync useEffect AND setLang would silently no-op on
  // any URL the user typed with a trailing slash (or that an OAuth
  // provider redirected back with).
  const normalizePath = (p: string): string => p.replace(/\/+$/, '') || '/';
  const prevPath = useRef<string | null>(null);
  useEffect(() => {
    const path = normalizePath(location.pathname);
    const previousPath = prevPath.current;
    prevPath.current = path;
    // Skip the sync on:
    //   - Initial mount (previousPath === null) — detectLang already set
    //     initialLang from path/navigator/localStorage/URL at boot.
    //   - Strict Mode re-invocation (previousPath === path, refs persist
    //     across the synthetic unmount/re-mount).
    //   - Effect re-runs triggered by lang change alone (path unchanged).
    if (previousPath === null || previousPath === path) return;
    const isEnMirror = isPublicEnMirror(path);
    const isZhMirror = isPublicZhMirror(path);
    if (isEnMirror && lang !== 'en') setLangState('en');
    else if (isZhMirror && lang !== 'zh') setLangState('zh');
    // else: private / non-mirrored route — keep current lang (respects
    // persistLang localStorage preference; user's last toggle persists).
  }, [location.pathname, lang]);

  const setLang = (newLang: Lang) => {
    setLangState(newLang);
    persistLang(newLang);

    // Normalize for mirror lookup so trailing-slash variants
    // (`/app/privacy/`, `/zh/app/terms/`) still navigate. The compared
    // value is the URL pathname as React Router exposes it; we then
    // navigate to the canonical public mirror when this path has one.
    const rawPath = location.pathname;
    const path = normalizePath(rawPath);
    const search = location.search;
    const hash = location.hash;

    let targetPath: string | null = null;
    if (newLang === 'zh') {
      targetPath = toZhMirrorPath(path) ?? null;
    } else {
      targetPath = toEnMirrorPath(path) ?? null;
    }

    if (targetPath && normalizePath(targetPath) !== path) {
      navigate(`${targetPath}${search}${hash}`);
    }
  };

  return (
    <LangContext.Provider value={{ lang, setLang }}>
      {children}
    </LangContext.Provider>
  );
}
